import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
} from '@mui/material';
import { formatNumber } from '../../helpers/numberUtils';
import { fetchProducts, ProductRow } from '../../helpers/adEvaluationData';
import moment from 'moment';

type SortKey =
  | 'total_conversions'
  | 'total_attendees'
  | 'total_revenue'
  | 'concept'
  | 'placement'
  | 'product_type';
type SortDir = 'asc' | 'desc';

const ProductsTab: React.FC = () => {
  const [startDate, setStartDate] = useState(moment().subtract(3, 'months').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ProductRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('total_conversions');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchProducts(new Date(startDate), new Date(endDate));
        if (!cancelled) setData(result);
      } catch (err) {
        console.error('Error fetching products:', err);
        if (!cancelled) setError('Fehler beim Laden der Produktdaten');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      const cmp =
        typeof aVal === 'number'
          ? (aVal as number) - (bVal as number)
          : String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const summaryRow = useMemo(() => {
    if (data.length === 0) return null;
    return {
      total_conversions: data.reduce((s, r) => s + Number(r.total_conversions), 0),
      total_attendees: data.reduce((s, r) => s + Number(r.total_attendees), 0),
      total_revenue: data.reduce((s, r) => s + Number(r.total_revenue), 0),
    };
  }, [data]);

  return (
    <Box>
      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="Von"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Bis"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Alert severity="info">Keine Produkt-Conversions im gewählten Zeitraum.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  <TableSortLabel
                    active={sortKey === 'placement'}
                    direction={sortKey === 'placement' ? sortDir : 'asc'}
                    onClick={() => handleSort('placement')}
                  >
                    Platzierung
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  <TableSortLabel
                    active={sortKey === 'product_type'}
                    direction={sortKey === 'product_type' ? sortDir : 'asc'}
                    onClick={() => handleSort('product_type')}
                  >
                    Typ
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  <TableSortLabel
                    active={sortKey === 'concept'}
                    direction={sortKey === 'concept' ? sortDir : 'asc'}
                    onClick={() => handleSort('concept')}
                  >
                    Concept
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  Produktname
                </TableCell>
                <Tooltip
                  title="Webinar: Registrierungen, PLX: Bestellungen (PROCESSING)"
                  arrow
                  placement="top"
                >
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                      cursor: 'help',
                    }}
                    align="right"
                  >
                    <TableSortLabel
                      active={sortKey === 'total_conversions'}
                      direction={sortKey === 'total_conversions' ? sortDir : 'desc'}
                      onClick={() => handleSort('total_conversions')}
                    >
                      Conversions
                    </TableSortLabel>
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title="Tatsächliche Webinar-Teilnehmer (nur bei Webinar-Typ)"
                  arrow
                  placement="top"
                >
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                      cursor: 'help',
                    }}
                    align="right"
                  >
                    <TableSortLabel
                      active={sortKey === 'total_attendees'}
                      direction={sortKey === 'total_attendees' ? sortDir : 'desc'}
                      onClick={() => handleSort('total_attendees')}
                    >
                      Teilnehmer
                    </TableSortLabel>
                  </TableCell>
                </Tooltip>
                <Tooltip title="Umsatz aus PLX-Bestellungen" arrow placement="top">
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                      cursor: 'help',
                    }}
                    align="right"
                  >
                    <TableSortLabel
                      active={sortKey === 'total_revenue'}
                      direction={sortKey === 'total_revenue' ? sortDir : 'desc'}
                      onClick={() => handleSort('total_revenue')}
                    >
                      Umsatz
                    </TableSortLabel>
                  </TableCell>
                </Tooltip>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  Erste Conv.
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  Letzte Conv.
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, idx) => (
                <TableRow key={`${row.placement}-${row.product_type}-${row.concept}-${idx}`}>
                  <TableCell>
                    <Chip
                      label={row.placement}
                      size="small"
                      color={row.placement === 'DOI' ? 'primary' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.product_type === 'webinar' ? 'Webinar' : 'PLX'}
                      size="small"
                      color={row.product_type === 'webinar' ? 'info' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {row.concept || '–'}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.product_name || '–'}</TableCell>
                  <TableCell align="right">
                    {formatNumber(Number(row.total_conversions), 0)}
                  </TableCell>
                  <TableCell align="right">
                    {row.product_type === 'webinar'
                      ? formatNumber(Number(row.total_attendees), 0)
                      : '–'}
                  </TableCell>
                  <TableCell align="right">
                    {Number(row.total_revenue) > 0
                      ? formatNumber(Number(row.total_revenue), 2) + ' €'
                      : '–'}
                  </TableCell>
                  <TableCell>
                    {row.first_conversion ? moment(row.first_conversion).format('DD.MM.YYYY') : '–'}
                  </TableCell>
                  <TableCell>
                    {row.last_conversion ? moment(row.last_conversion).format('DD.MM.YYYY') : '–'}
                  </TableCell>
                </TableRow>
              ))}
              {summaryRow && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                    Insgesamt
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryRow.total_conversions, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryRow.total_attendees, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryRow.total_revenue, 2)} €
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProductsTab;
